/**
 * Partner mode (S3): the app served on a partner's own domain, e.g.
 * community.alertboard.app. soltik-web injects window.__SOLTIK_PARTNER__ into
 * the page on a verified partner domain (workers/soltik-web servePartnerSite);
 * main.tsx then renders PartnerApp instead of the full soltik app.
 *
 * Everything here is display only: the API decides what a partner host may
 * do from the X-Partner-Host header soltik-web adds, never from the browser.
 *
 * Sign-in (central sign-in, soltik-api lib/partner-session.ts): a partner
 * domain has no sign-in form and holds no soltik session. "Sign in" goes to
 * /auth/partner/start, which soltik-web answers with soltik.app's sign-in page
 * for this partner; the session comes back as an HttpOnly cookie that only
 * soltik-web reads. The page learns whether someone is signed in from
 * `signed_in`, and installPartnerSession gives the rest of the app (which reads
 * the token store) a placeholder that carries no credential.
 */
import { clearAllSessions, setToken } from '../utils/secureToken';

export interface PartnerSite {
  host: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
  default_locale: string | null;
  powered_by_soltik: boolean;
  /** Someone is signed in on this domain (soltik-web, from the session cookie). Display only. */
  signed_in: boolean;
  user_id: string | null;
  /** soltik.app (or sta.soltik.app): where sign-in and the rest of soltik live. */
  soltik_origin: string;
  /** 'own': a white-label partner signs people in on this domain (/signin); else on soltik.app. */
  signin: 'own' | 'central';
  /** This domain's own Turnstile widget, with signin 'own'. */
  turnstile_site_key: string | null;
}

export const PARTNER_REGIONS = ['il', 'ua', 'us'] as const;

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** soltik's own origin, never anything else: sign-in links are built from it. */
function soltikOrigin(raw: unknown): string {
  try {
    const u = new URL(String(raw));
    if (u.protocol === 'https:' && (u.hostname === 'soltik.app' || u.hostname.endsWith('.soltik.app')) && !u.port) return u.origin;
  } catch { /* fall through */ }
  return 'https://soltik.app';
}

export function getPartnerSite(): PartnerSite | null {
  const raw = (window as unknown as { __SOLTIK_PARTNER__?: Partial<PartnerSite> }).__SOLTIK_PARTNER__;
  if (!raw || typeof raw !== 'object' || typeof raw.host !== 'string' || raw.host !== window.location.hostname) return null;
  return {
    host: raw.host,
    name: typeof raw.name === 'string' && raw.name ? raw.name : raw.host,
    logo_url: typeof raw.logo_url === 'string' && raw.logo_url.startsWith('https://') ? raw.logo_url : null,
    primary_color: typeof raw.primary_color === 'string' && COLOR_RE.test(raw.primary_color) ? raw.primary_color : null,
    accent_color: typeof raw.accent_color === 'string' && COLOR_RE.test(raw.accent_color) ? raw.accent_color : null,
    default_locale: typeof raw.default_locale === 'string' ? raw.default_locale : null,
    powered_by_soltik: raw.powered_by_soltik !== false,
    signed_in: raw.signed_in === true && typeof raw.user_id === 'string' && UUID_RE.test(raw.user_id),
    user_id: raw.signed_in === true && typeof raw.user_id === 'string' && UUID_RE.test(raw.user_id) ? raw.user_id : null,
    soltik_origin: soltikOrigin(raw.soltik_origin),
    signin: raw.signin === 'own' && typeof raw.turnstile_site_key === 'string' && raw.turnstile_site_key ? 'own' : 'central',
    turnstile_site_key: raw.signin === 'own' && typeof raw.turnstile_site_key === 'string' && raw.turnstile_site_key ? raw.turnstile_site_key : null,
  };
}

/** The placeholder session the partner app's token store holds: the user id, and no credential. */
export const PARTNER_SESSION_PLACEHOLDER_SIG = 'partner-session';
export function partnerSessionPlaceholder(userId: string): string {
  const payload = btoa(JSON.stringify({ sub: userId, typ: 'partner_session' })).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
  return `p.${payload}.${PARTNER_SESSION_PLACEHOLDER_SIG}`;
}

/**
 * On a partner domain, the token store mirrors the domain's session: every soltik
 * session left in this origin's storage (from before central sign-in) is removed,
 * and a signed-in visitor gets the placeholder, so pages that ask "signed in?" by
 * reading the store get the right answer. soltik-web drops whatever Authorization
 * the browser sends here; the session is the HttpOnly cookie.
 */
export function installPartnerSession(site: PartnerSite): void {
  try {
    clearAllSessions();
    if (site.signed_in && site.user_id) setToken(partnerSessionPlaceholder(site.user_id));
  } catch { /* storage blocked: signed-out look, the cookie still works */ }
}

/** Where "Sign in" goes on this domain: its own page (white label), or soltik.app's (central). */
export const signInHref = (site: Pick<PartnerSite, 'signin'>, path: string, lang: string) =>
  site.signin === 'own' ? `/signin?return=${encodeURIComponent(path.startsWith('/') && !path.startsWith('//') ? path : '/')}` : partnerSignInUrl(path, lang);

/** Central sign-in: soltik-web's start, which returns here to `path` afterwards. */
export const partnerSignInUrl = (path: string, lang: string) =>
  `/auth/partner/start?return=${encodeURIComponent(path.startsWith('/') && !path.startsWith('//') ? path : '/')}&lang=${encodeURIComponent(lang)}`;

export const isPartnerMode = (): boolean => getPartnerSite() !== null;

/** Text colour that reads on `hex` (#rrggbb): dark on light brands, white on dark ones. */
export function onColor(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? '#0c0c12' : '#ffffff';
}

/** A full page load (sign-in pages reload the app with the session). */
export function hardNavigate(url: string): void {
  window.location.assign(url);
}
